import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import api from '@/lib/api';
import * as faceapi from 'face-api.js';
import { 
  Camera, CheckCircle, XCircle, AlertTriangle, 
  Loader2, Shield, Eye, RefreshCw, User
} from 'lucide-react';

// Componente de Cadastro Facial Mobile
export function CadastroFacialMobile({ autorizadoId, autorizadoNome, onSuccess, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [etapa, setEtapa] = useState('intro'); // intro, termo, captura, prova_vida, processando, sucesso, erro
  const [stream, setStream] = useState(null);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [provaVida, setProvaVida] = useState({ piscar: false, virar: false });
  const [faceDescriptor, setFaceDescriptor] = useState(null);
  const [progresso, setProgresso] = useState(0);
  const [erro, setErro] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);

  // Carregar modelos face-api.js
  useEffect(() => {
    const loadModels = async () => {
      try {
        const MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.12/model';
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
          faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
        ]);
        setModelsLoaded(true);
      } catch (error) {
        console.error('Erro ao carregar modelos:', error);
        setErro('Erro ao carregar sistema de reconhecimento');
      }
    };
    loadModels();
  }, []);

  // Iniciar câmera
  const iniciarCamera = async () => {
    try {
      setCameraReady(false);
      setEtapa('captura');
      
      // Aguardar um pouco para o DOM atualizar
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'user', 
          width: { ideal: 640 }, 
          height: { ideal: 480 } 
        }
      });
      
      setStream(mediaStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().then(() => {
            setCameraReady(true);
          }).catch(err => {
            console.error('Erro ao iniciar vídeo:', err);
          });
        };
      }
    } catch (error) {
      console.error('Erro ao acessar câmera:', error);
      if (error.name === 'NotAllowedError') {
        setErro('Permissão de câmera negada. Por favor, permita o acesso à câmera nas configurações do navegador.');
      } else if (error.name === 'NotFoundError') {
        setErro('Nenhuma câmera encontrada no dispositivo.');
      } else if (error.name === 'NotReadableError') {
        setErro('A câmera está sendo usada por outro aplicativo.');
      } else {
        setErro('Não foi possível acessar a câmera: ' + error.message);
      }
      setEtapa('erro');
    }
  };

  // Parar câmera
  const pararCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  }, [stream]);

  useEffect(() => {
    return () => pararCamera();
  }, [pararCamera]);

  // Detectar face e fazer prova de vida
  const detectarFace = async () => {
    if (!videoRef.current || !modelsLoaded) return;

    const detection = await faceapi
      .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions())
      .withFaceLandmarks()
      .withFaceDescriptor();

    if (detection) {
      setProgresso(prev => Math.min(prev + 10, 100));
      
      // Simular prova de vida
      if (progresso > 30 && !provaVida.piscar) {
        setProvaVida(prev => ({ ...prev, piscar: true }));
        toast.info('Pisque os olhos', { icon: '👁️' });
      }
      if (progresso > 60 && !provaVida.virar) {
        setProvaVida(prev => ({ ...prev, virar: true }));
        toast.info('Vire levemente o rosto', { icon: '↔️' });
      }
      
      if (progresso >= 100) {
        setFaceDescriptor(Array.from(detection.descriptor));
        setEtapa('processando');
        await salvarFace(Array.from(detection.descriptor));
      }
    }
  };

  // Intervalo de detecção
  useEffect(() => {
    if (etapa === 'captura' && modelsLoaded && cameraReady) {
      const interval = setInterval(detectarFace, 500);
      return () => clearInterval(interval);
    }
  }, [etapa, modelsLoaded, progresso, cameraReady]);

  // Salvar face no backend
  const salvarFace = async (descriptor) => {
    try {
      await api.put(`/dnago/autorizados/${autorizadoId}/face`, {
        face_template: JSON.stringify(descriptor),
        consent_date: new Date().toISOString()
      });
      setEtapa('sucesso');
      toast.success('Face cadastrada com sucesso!');
      onSuccess?.();
    } catch (error) {
      setErro('Erro ao salvar dados faciais');
      setEtapa('erro');
    }
  };

  const renderEtapa = () => {
    switch (etapa) {
      case 'intro':
        return (
          <div className="text-center space-y-6 p-6">
            <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
              <Camera className="w-12 h-12 text-purple-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Cadastro Facial</h2>
              <p className="text-gray-500 mt-2">
                Vamos cadastrar sua face para autorização segura de retirada
              </p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg text-left text-sm">
              <p className="font-medium text-purple-900 mb-2">O que vai acontecer:</p>
              <ul className="space-y-1 text-purple-700">
                <li>• Aceite do termo de uso</li>
                <li>• Captura da sua face pela câmera</li>
                <li>• Prova de vida (piscar e virar o rosto)</li>
                <li>• Validação automática</li>
              </ul>
            </div>
            <Button 
              className="w-full bg-purple-600 hover:bg-purple-700 h-12 text-lg"
              onClick={() => setEtapa('termo')}
            >
              Começar Cadastro
            </Button>
          </div>
        );

      case 'termo':
        return (
          <div className="space-y-4 p-6">
            <div className="flex items-center gap-2 text-purple-600">
              <Shield className="w-5 h-5" />
              <h3 className="font-semibold">Termo de Consentimento - LGPD</h3>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg text-sm max-h-48 overflow-y-auto">
              <p className="mb-2">
                Ao prosseguir, você autoriza a coleta e armazenamento dos seus dados biométricos faciais para fins de identificação na retirada de alunos.
              </p>
              <p className="mb-2">
                Seus dados serão:
              </p>
              <ul className="list-disc list-inside space-y-1 mb-2">
                <li>Armazenados de forma criptografada</li>
                <li>Utilizados exclusivamente para verificação de identidade</li>
                <li>Podem ser excluídos a qualquer momento</li>
              </ul>
              <p>
                Este processo está em conformidade com a Lei Geral de Proteção de Dados (LGPD).
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={onClose}>
                Não Aceito
              </Button>
              <Button 
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                onClick={iniciarCamera}
              >
                Aceito e Continuar
              </Button>
            </div>
          </div>
        );

      case 'captura':
        return (
          <div className="space-y-4 p-4">
            <div className="relative rounded-xl overflow-hidden bg-black aspect-[4/3]">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className={`w-full h-full object-cover ${cameraReady ? 'opacity-100' : 'opacity-0'}`}
              />
              {/* Loading da câmera */}
              {!cameraReady && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900">
                  <Loader2 className="w-12 h-12 animate-spin text-purple-400 mb-4" />
                  <p className="text-white text-sm">Iniciando câmera...</p>
                  <p className="text-gray-400 text-xs mt-2">Aguarde ou permita o acesso</p>
                </div>
              )}
              {/* Overlay de guia - só mostra quando câmera está pronta */}
              {cameraReady && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-48 h-64 border-4 border-dashed border-white/50 rounded-full" />
                </div>
              )}
              {/* Indicadores */}
              {cameraReady && (
                <div className="absolute bottom-4 left-4 right-4 space-y-2">
                  <Progress value={progresso} className="h-2" />
                  <div className="flex gap-2 justify-center">
                    <Badge className={provaVida.piscar ? 'bg-green-500' : 'bg-gray-400'}>
                      {provaVida.piscar ? <CheckCircle className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                      Piscar
                    </Badge>
                    <Badge className={provaVida.virar ? 'bg-green-500' : 'bg-gray-400'}>
                      {provaVida.virar ? <CheckCircle className="w-3 h-3 mr-1" /> : '↔️'}
                      Virar
                    </Badge>
                  </div>
                </div>
              )}
            </div>
            <p className="text-center text-sm text-gray-500">
              {cameraReady 
                ? 'Posicione seu rosto dentro do círculo e siga as instruções'
                : 'Aguardando acesso à câmera...'}
            </p>
          </div>
        );

      case 'processando':
        return (
          <div className="text-center space-y-4 p-6">
            <Loader2 className="w-16 h-16 animate-spin text-purple-600 mx-auto" />
            <p className="text-lg">Processando dados faciais...</p>
          </div>
        );

      case 'sucesso':
        return (
          <div className="text-center space-y-6 p-6">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-green-600">Sucesso!</h2>
              <p className="text-gray-500 mt-2">
                Face cadastrada com sucesso. Agora você pode usar o reconhecimento facial para retiradas.
              </p>
            </div>
            <Button className="bg-green-600 hover:bg-green-700" onClick={onClose}>
              Concluir
            </Button>
          </div>
        );

      case 'erro':
        return (
          <div className="text-center space-y-6 p-6">
            <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-red-600">Erro</h2>
              <p className="text-gray-500 mt-2">{erro}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button onClick={() => { setEtapa('intro'); setErro(null); setProgresso(0); }}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Tentar Novamente
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0">
        {renderEtapa()}
      </DialogContent>
    </Dialog>
  );
}

// Componente de Verificação Facial na Portaria
export function VerificacaoFacial({ autorizado, onVerified, onClose }) {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [verificando, setVerificando] = useState(false);
  const [resultado, setResultado] = useState(null); // { match: boolean, score: number }

  useEffect(() => {
    const loadModels = async () => {
      try {
        const MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.12/model';
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
          faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
        ]);
        setModelsLoaded(true);
        iniciarCamera();
      } catch (error) {
        console.error('Erro ao carregar modelos:', error);
      }
    };
    loadModels();
  }, []);

  const iniciarCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 640, height: 480 }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Erro ao acessar câmera:', error);
    }
  };

  const pararCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  }, [stream]);

  useEffect(() => {
    return () => pararCamera();
  }, [pararCamera]);

  const verificarFace = async () => {
    if (!videoRef.current || !modelsLoaded || !autorizado?.face_template) {
      toast.error('Não foi possível verificar. Autorizado sem face cadastrada.');
      return;
    }

    setVerificando(true);
    setResultado(null);

    try {
      // Detectar face atual
      const detection = await faceapi
        .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (!detection) {
        setResultado({ match: false, score: 0, erro: 'Nenhuma face detectada' });
        return;
      }

      // Comparar com template salvo
      const savedDescriptor = JSON.parse(autorizado.face_template);
      const distance = faceapi.euclideanDistance(detection.descriptor, savedDescriptor);
      const score = Math.max(0, Math.round((1 - distance) * 100));
      const match = distance < 0.6; // Threshold de 60%

      setResultado({ match, score });

      // Registrar verificação no backend
      await api.post('/dnago/face-verification-log', {
        autorizado_id: autorizado.id,
        match,
        score,
        timestamp: new Date().toISOString()
      }).catch(() => {});

    } catch (error) {
      console.error('Erro na verificação:', error);
      setResultado({ match: false, score: 0, erro: 'Erro na verificação' });
    } finally {
      setVerificando(false);
    }
  };

  const getResultadoUI = () => {
    if (!resultado) return null;

    if (resultado.erro) {
      return (
        <div className="bg-amber-100 border-2 border-amber-500 rounded-xl p-4 text-center">
          <AlertTriangle className="w-10 h-10 text-amber-600 mx-auto mb-2" />
          <p className="text-amber-700 font-medium">{resultado.erro}</p>
          <p className="text-sm text-amber-600">Tente novamente ou faça verificação manual</p>
        </div>
      );
    }

    if (resultado.match) {
      return (
        <div className="bg-green-100 border-2 border-green-500 rounded-xl p-4 text-center">
          <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-2" />
          <p className="text-green-700 font-bold text-lg">Identidade Confirmada</p>
          <p className="text-sm text-green-600">Compatibilidade: {resultado.score}%</p>
        </div>
      );
    }

    return (
      <div className="bg-red-100 border-2 border-red-500 rounded-xl p-4 text-center">
        <XCircle className="w-12 h-12 text-red-600 mx-auto mb-2" />
        <p className="text-red-700 font-bold text-lg">Face Não Compatível</p>
        <p className="text-sm text-red-600">Compatibilidade: {resultado.score}%</p>
        <p className="text-xs text-red-500 mt-2">Verifique manualmente com documento</p>
      </div>
    );
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-purple-600" />
            Verificação Facial
          </DialogTitle>
          <DialogDescription>
            Verificando identidade de {autorizado?.nome}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Vídeo */}
          <div className="relative rounded-xl overflow-hidden bg-black aspect-[4/3]">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            {verificando && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <Loader2 className="w-12 h-12 animate-spin text-white" />
              </div>
            )}
          </div>

          {/* Resultado */}
          {getResultadoUI()}

          {/* Ações */}
          <div className="flex gap-2">
            {!resultado?.match && (
              <Button 
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                onClick={verificarFace}
                disabled={verificando}
              >
                {verificando ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Camera className="w-4 h-4 mr-2" />
                )}
                Verificar Face
              </Button>
            )}
            {resultado?.match && (
              <Button 
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={() => onVerified(resultado)}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Confirmar Retirada
              </Button>
            )}
            <Button variant="outline" onClick={onClose}>
              {resultado?.match ? 'Fechar' : 'Verificar Manualmente'}
            </Button>
          </div>

          {!resultado?.match && (
            <p className="text-xs text-center text-gray-500">
              A verificação facial é opcional. Você pode autorizar manualmente.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default { CadastroFacialMobile, VerificacaoFacial };
